import { max, min, sum } from "lodash";
import { useRef, useState } from "react";

import { FilterTypes } from "../enums/components";
import {
  aggregationFunctionsTypes,
  chartTypesEnums,
  colorCategoriesEnums,
  diagramTypes,
  diagramTypesDisplayOptions,
  getIndividualChartFitlersConfig,
  getSortOptions,
  INDIVIDUAL_CHART_FILTERS_KEY,
  valuesEnums,
} from "../enums/dynamicDashboard";
import { findDataRange, getUniqueValuesFromJson, isNumeric } from "./dataManipulation";
import { capitalizeFirstLetter, formatNumberBasedOnUser } from "./namesManipulation";
import { cn } from "./styling";

export const handleConfigChange = (index, diagrams, key, newValue, updateChart) => {
  const newConfig = { ...diagrams[index], [key]: newValue };
  const updatedConfig = diagrams?.map((diagram, i) => {
    return i !== index ? diagram : newConfig;
  });
  updateChart("diagrams", updatedConfig);
};

export const diagramInitialConfig = {
  color: "black",
  dataKey: null,
  dataPoint: null,
  diagramType: null,
  direction: "horizontal",
  partitions: null,
  percentageBased: false,
  stacks: null,
  title: null,
};

export const settingsOptions = Object.freeze({
  AGGREGATION_TYPE: "aggregationType",
  COLOR: "color",
  COLOR_BAR_RANGE: "colorBarRange",
  DATA_POINT: "dataPoint",
  DESCRIPTION: "description",
  DIAGRAM_TYPE: "diagramType",
  DIAGRAMS: "diagrams",
  DIAGRAMS_AS_SECOND_Y_AXIS: "asSecondYAxis",
  DIAGRAMS_COLOR: "color",
  DIAGRAMS_DIAGRAM_DATA_KEY: "dataKey",
  DIAGRAMS_DIAGRAM_TYPE: "diagramType",
  DIAGRAMS_DIRECTION: "direction",
  DIAGRAMS_PARTITIONS: "partitions",
  DIAGRAMS_STACK_ID: "stackId",
  DIAGRAMS_STACKS: "stacks",
  DIRECTION: "direction",
  HEIGHT_100: "height100",
  INVERT_X_AXIS: "invertXAxis",
  INVERT_Y_AXIS: "invertYAxis",
  IS_WORKING_DAY: "isWorkingDay",
  IS_WORKING_HOUR: "isWorkingHour",
  LEGENDS_POSITION: "legendsPosition",
  PARTITIONS: "partitions",
  PER_M2: "perM2",
  SCALING_TYPE: "scalingType",
  SECOND_Y_AXIS_NAME: "secondYAxisName",
  SECOND_Y_AXIS_RANGE: "secondYAxisRange",
  SHOW_PIE_LABELS: "showPieLabels",
  SORT_BY: "sortBy",
  SORT_VALUE: "sortValue",
  STACKS: "stacks",
  SUB_DATAPOINT: "subDataPoint",
  TITLE: "title",
  X: "x",
  X_AXIS: "xAxis",
  X_AXIS_NAME: "xAxisName",
  X_AXIS_RANGE: "xAxisRange",
  Y: "y",
  Y_AXIS: "yAxis",
  Y_AXIS_NAME: "yAxisName",
  Y_AXIS_RANGE: "yAxisRange",
});

const settingsOptionsDisplayName = Object.freeze({
  [settingsOptions.AGGREGATION_TYPE]: "Aggregation Type",
  [settingsOptions.COLOR]: "Color",
  [settingsOptions.COLOR_BAR_RANGE]: "Color Bar Range",
  [settingsOptions.DATA_POINT]: "Data Point",
  [settingsOptions.DESCRIPTION]: "Description",
  [settingsOptions.DIAGRAM_TYPE]: "Diagram Type",
  [settingsOptions.DIAGRAMS]: "Diagrams",
  [settingsOptions.DIAGRAMS_AS_SECOND_Y_AXIS]: "As Second Y Axis",
  [settingsOptions.DIAGRAMS_COLOR]: "Color",
  [settingsOptions.DIAGRAMS_DIAGRAM_DATA_KEY]: "Data Key",
  [settingsOptions.DIAGRAMS_DIAGRAM_TYPE]: "Diagram Type",
  [settingsOptions.DIAGRAMS_DIRECTION]: "Direction",
  [settingsOptions.DIAGRAMS_PARTITIONS]: "Partitions",
  [settingsOptions.DIAGRAMS_STACK_ID]: "Stack ID",
  [settingsOptions.DIAGRAMS_STACKS]: "Stacks",
  [settingsOptions.DIRECTION]: "Direction",
  [settingsOptions.HEIGHT_100]: "Height 100",
  [settingsOptions.INVERT_X_AXIS]: "Invert X Axis",
  [settingsOptions.INVERT_Y_AXIS]: "Invert Y Axis",
  [settingsOptions.IS_WORKING_DAY]: "Is Working Day",
  [settingsOptions.IS_WORKING_HOUR]: "Is Working Hour",
  [settingsOptions.LEGENDS_POSITION]: "Legends Position",
  [settingsOptions.PARTITIONS]: "Partitions",
  [settingsOptions.PER_M2]: "Per M2",
  [settingsOptions.SCALING_TYPE]: "Scaling Type",
  [settingsOptions.SECOND_Y_AXIS_NAME]: "2nd Y Axis Name",
  [settingsOptions.SECOND_Y_AXIS_RANGE]: "2nd Y Axis Range",
  [settingsOptions.SHOW_PIE_LABELS]: "Show Pie Labels",
  [settingsOptions.SORT_BY]: "Sort By",
  [settingsOptions.SORT_VALUE]: "Sort Value",
  [settingsOptions.STACKS]: "Stacks",
  [settingsOptions.SUB_DATAPOINT]: "Sub Data Point",
  [settingsOptions.TITLE]: "Title",
  [settingsOptions.X]: "X",
  [settingsOptions.X_AXIS]: "X Axis",
  [settingsOptions.X_AXIS_NAME]: "X Axis Name",
  [settingsOptions.X_AXIS_RANGE]: "X Axis Range",
  [settingsOptions.Y]: "Y",
  [settingsOptions.Y_AXIS]: "Y Axis",
  [settingsOptions.Y_AXIS_NAME]: "Y Axis Name",
  [settingsOptions.Y_AXIS_RANGE]: "Y Axis Range",
});

const getSettingOptionDisplayName = ({ key }) => {
  return settingsOptionsDisplayName[key];
};

export const defaultIsVisible = (chartType, key) => isRelevantOption(chartType, key);

const getSettingItemCommonPart = ({ config, filterType, isInDiagramsPart, key, props, ...rest }) => {
  return {
    adjustConfig: (config) => config,
    filterLabel: getSettingOptionDisplayName({ key }),
    filterName: key,
    filterType,
    isInDiagramsPart,
    props: {
      debounceTime: 500,
      height: "4rem",
      label: getSettingOptionDisplayName({ key }),
      placeholder: getSettingOptionDisplayName({ key }),
      ...props,
      className: cn("w-3/4", props?.className),
    },
    setConfig: (updateChart) => updateChart,
    visible: () => defaultIsVisible(config.chartType, key),
    ...rest,
  };
};

export const hasSecondYAxis = ({ diagrams }) => {
  return diagrams?.some((diagram) => diagram[settingsOptions.DIAGRAMS_AS_SECOND_Y_AXIS]);
};

const dynamicDahboardDefaultSettings = (updateChart, config, rest = {}) => ({
  [settingsOptions.AGGREGATION_TYPE]: {
    ...getSettingItemCommonPart({
      config,
      filterType: FilterTypes.DROPDOWN_SINGLE,
      key: settingsOptions.AGGREGATION_TYPE,
      props: {
        options: [
          aggregationFunctionsTypes.SUM,
          aggregationFunctionsTypes.AVG,
          aggregationFunctionsTypes.MAX,
          aggregationFunctionsTypes.MIN,
        ],
      },
      ...rest,
    }),
  },
  [settingsOptions.DESCRIPTION]: {
    ...getSettingItemCommonPart({
      config,
      filterType: FilterTypes.INPUT,
      key: settingsOptions.DESCRIPTION,
      props: {
        height: "auto",
        multiline: true,
      },
      ...rest,
    }),
  },
  [settingsOptions.DIAGRAMS_AS_SECOND_Y_AXIS]: {
    ...getSettingItemCommonPart({
      adjustSetConfigFn: (index) => (filterName, value) =>
        handleConfigChange(index, config.diagrams, filterName, value, updateChart),
      config,
      filterType: FilterTypes.DROPDOWN_SINGLE,
      isInDiagramsPart: true,
      key: settingsOptions.DIAGRAMS_AS_SECOND_Y_AXIS,
      props: {
        displayOptions: ["No", "Yes"],
        options: [0, 1],
      },
      visible: (diagramIndex) =>
        isRelevantOption(config.chartType, settingsOptions.DIAGRAMS_AS_SECOND_Y_AXIS, diagramIndex),
      ...rest,
    }),
  },
  [settingsOptions.DIAGRAMS_DIAGRAM_TYPE]: {
    ...getSettingItemCommonPart({
      adjustSetConfigFn: (index) => (filterName, value) =>
        handleConfigChange(index, config.diagrams, filterName, value, updateChart),
      config,
      filterType: FilterTypes.DROPDOWN_SINGLE,
      isInDiagramsPart: true,
      key: settingsOptions.DIAGRAMS_DIAGRAM_TYPE,
      props: {
        displayOptions: Object.values(diagramTypesDisplayOptions),
        options: Object.values(diagramTypes),
      },
      visible: (diagramIndex) => isRelevantOption(config.chartType, settingsOptions.DIAGRAM_TYPE, diagramIndex),
      ...rest,
    }),
  },
  [settingsOptions.DIAGRAMS_DIRECTION]: {
    ...getSettingItemCommonPart({
      adjustSetConfigFn: (index) => (filterName, value) =>
        handleConfigChange(index, config.diagrams, filterName, value, updateChart),
      config,
      filterType: FilterTypes.DROPDOWN_SINGLE,
      isInDiagramsPart: true,
      key: settingsOptions.DIAGRAMS_DIRECTION,
      props: {
        displayOptions: ["Horizontal", "Vertical"],
        options: ["horizontal", "vertical"],
      },
      visible: (diagramIndex) => isRelevantOption(config.chartType, settingsOptions.DIRECTION, diagramIndex),
      ...rest,
    }),
  },
  [settingsOptions.DIAGRAMS_STACK_ID]: {
    ...getSettingItemCommonPart({
      adjustSetConfigFn: (index) => (filterName, value) =>
        handleConfigChange(index, config.diagrams, filterName, value, updateChart),
      config,
      filterType: FilterTypes.INPUT,
      isInDiagramsPart: true,
      key: settingsOptions.DIAGRAMS_STACK_ID,
      visible: (diagramIndex) =>
        isRelevantOption(config.chartType, settingsOptions.DIAGRAMS_STACK_ID, diagramIndex) &&
        config?.diagrams?.[diagramIndex]?.diagramType === diagramTypes.BAR,
      ...rest,
    }),
  },
  [settingsOptions.INVERT_X_AXIS]: {
    ...getSettingItemCommonPart({
      config,
      filterType: FilterTypes.DROPDOWN_SINGLE,
      key: settingsOptions.INVERT_X_AXIS,
      props: {
        displayOptions: ["No", "Yes"],
        options: [0, 1],
      },
      ...rest,
    }),
  },
  [settingsOptions.INVERT_Y_AXIS]: {
    ...getSettingItemCommonPart({
      config,
      filterType: FilterTypes.DROPDOWN_SINGLE,
      key: settingsOptions.INVERT_Y_AXIS,
      props: {
        displayOptions: ["No", "Yes"],
        options: [0, 1],
      },
      ...rest,
    }),
  },
  [settingsOptions.LEGENDS_POSITION]: {
    ...getSettingItemCommonPart({
      config,
      filterType: FilterTypes.DROPDOWN_SINGLE,
      key: settingsOptions.LEGENDS_POSITION,
      props: {
        displayOptions: ["Bottom", "Right"],
        options: ["bottom", "right"],
      },
      ...rest,
    }),
  },
  [settingsOptions.SECOND_Y_AXIS_NAME]: {
    ...getSettingItemCommonPart({
      config,
      filterType: FilterTypes.INPUT,
      key: settingsOptions.SECOND_Y_AXIS_NAME,
      visible: () => isRelevantOption(config.chartType, settingsOptions.SECOND_Y_AXIS_NAME) && hasSecondYAxis(config),
      ...rest,
    }),
  },
  [settingsOptions.SECOND_Y_AXIS_RANGE]: {
    ...getSettingItemCommonPart({
      config,
      filterType: FilterTypes.NUMERIC_RANGE_PICKER,
      key: settingsOptions.SECOND_Y_AXIS_RANGE,
      props: {
        changeBySlider: false,
        changeByTyping: true,
      },
      visible: () => isRelevantOption(config.chartType, settingsOptions.SECOND_Y_AXIS_RANGE) && hasSecondYAxis(config),
      ...rest,
    }),
  },
  [settingsOptions.SHOW_PIE_LABELS]: {
    ...getSettingItemCommonPart({
      config,
      filterType: FilterTypes.DROPDOWN_SINGLE,
      key: settingsOptions.SHOW_PIE_LABELS,
      props: {
        displayOptions: ["No", "Yes"],
        options: [0, 1],
      },
      ...rest,
    }),
  },
  [settingsOptions.SORT_VALUE]: {
    ...getSettingItemCommonPart({
      config,
      filterType: FilterTypes.DROPDOWN_SINGLE,
      key: settingsOptions.SORT_VALUE,
      props: {
        displayOptions: getSortOptions({ chartType: config.chartType })?.displayOptions,
        options: getSortOptions({ chartType: config.chartType })?.options,
      },
      visible: () => {
        return (
          isRelevantOption(config.chartType, settingsOptions.SORT_BY) &&
          !(config.chartType === chartTypesEnums.COMPOSED)
        );
      },
      ...rest,
    }),
  },
  [settingsOptions.TITLE]: {
    ...getSettingItemCommonPart({
      config,
      filterType: FilterTypes.INPUT,
      key: settingsOptions.TITLE,
      ...rest,
    }),
  },
  [settingsOptions.X_AXIS_NAME]: {
    ...getSettingItemCommonPart({
      config,
      filterType: FilterTypes.INPUT,
      key: settingsOptions.X_AXIS_NAME,
      ...rest,
    }),
  },
  [settingsOptions.X_AXIS_RANGE]: {
    ...getSettingItemCommonPart({
      config,
      filterType: FilterTypes.NUMERIC_RANGE_PICKER,
      key: settingsOptions.X_AXIS_RANGE,
      props: {
        changeBySlider: false,
        changeByTyping: true,
      },
      ...rest,
    }),
  },
  [settingsOptions.Y_AXIS_NAME]: {
    ...getSettingItemCommonPart({
      config,
      filterType: FilterTypes.INPUT,
      key: settingsOptions.Y_AXIS_NAME,
      ...rest,
    }),
  },
  [settingsOptions.Y_AXIS_RANGE]: {
    ...getSettingItemCommonPart({
      config,
      filterType: FilterTypes.NUMERIC_RANGE_PICKER,
      key: settingsOptions.Y_AXIS_RANGE,
      props: {
        changeBySlider: false,
        changeByTyping: true,
      },
      ...rest,
    }),
  },
});

export const getDynamicDahboardDefaultSettings = ({ config, override, settingName, updateChart }) => {
  const settings = dynamicDahboardDefaultSettings(updateChart, config, override);
  return settings[settingName] ?? {};
};

export const aggregatedSumValue = ({ aggregateColumns, data, groupByColumns }) => {
  if (!data?.length || !aggregateColumns?.length) return [];
  let dataCopy = [...(data ?? [])];

  if (!groupByColumns?.filter((e) => e !== null && e !== undefined)?.length) {
    return [
      dataCopy?.reduce((acc, cur) => {
        aggregateColumns?.forEach((col) => {
          acc[col] = (acc[col] || 0) + (cur[col] || 0);
        });
        return acc;
      }, {}),
    ];
  }

  return Object.values(
    dataCopy?.reduce((acc, cur) => {
      const key = groupByColumns?.map((col) => cur[col]).join("|");
      if (!acc[key]) {
        acc[key] = Object.fromEntries(groupByColumns?.map((col) => [col, cur[col]]));
        aggregateColumns?.forEach((col) => {
          acc[key][col] = cur[col] || 0;
        });
      } else {
        aggregateColumns?.forEach((col) => {
          acc[key][col] += cur[col] || 0;
        });
      }
      return acc;
    }, {})
  );
};

export const aggregatedAvgValue = ({ aggregateColumns, data, groupByColumns }) => {
  if (!data?.length > 0 || !aggregateColumns?.length) return [];
  let dataCopy = [...(data ?? [])];

  if (!groupByColumns?.filter((e) => e !== null && e !== undefined)?.length) {
    const totals = dataCopy.reduce(
      (acc, cur) => {
        aggregateColumns.forEach((col) => {
          acc[col] = (acc[col] || 0) + (cur[col] || 0);
        });
        if (aggregateColumns.some((col) => isNumeric(cur[col]))) {
          acc._count += 1;
        }
        return acc;
      },
      { _count: 0 }
    );

    return [Object.fromEntries(aggregateColumns.map((col) => [col, totals[col] / totals._count]))];
  }

  const grouped = dataCopy.reduce((acc, cur) => {
    const key = groupByColumns.map((col) => cur[col]).join("|");

    if (aggregateColumns?.every((col) => !isNumeric(cur[col]))) return acc;

    if (!acc[key]) {
      acc[key] = {
        ...Object.fromEntries(groupByColumns.map((col) => [col, cur[col]])),
        _count: 1,
      };
      aggregateColumns.forEach((col) => {
        acc[key][col] = cur[col] || 0;
      });
    } else {
      acc[key]._count += 1;
      aggregateColumns.forEach((col) => {
        acc[key][col] += cur[col] || 0;
      });
    }

    return acc;
  }, {});

  return Object.values(grouped).map(({ _count, ...rest }) => {
    aggregateColumns.forEach((col) => {
      rest[col] = rest[col] / _count;
    });
    return rest;
  });
};

export const aggregatedFirstValue = ({ aggregateColumns, data, groupByColumns }) => {
  if (!data?.length > 0) return [];
  let dataCopy = [...(data ?? [])];

  if (!groupByColumns?.filter((e) => e !== null && e !== undefined)?.length) {
    return [
      dataCopy?.reduce((acc, cur) => {
        aggregateColumns.forEach((col) => {
          acc[col] = acc[col] !== undefined ? acc[col] : cur[col];
        });
        return acc;
      }, {}),
    ];
  }

  return Object.values(
    dataCopy?.reduce((acc, cur) => {
      const key = groupByColumns?.map((col) => cur[col]).join("|");
      if (!acc[key]) {
        acc[key] = Object.fromEntries(groupByColumns?.map((col) => [col, cur[col]]));
        aggregateColumns.forEach((col) => {
          acc[key][col] = acc[key][col] !== undefined ? acc[key][col] : cur[col];
        });
      }
      return acc;
    }, {})
  );
};

export const aggregatedMaxValue = ({ aggregateColumns, data, groupByColumns }) => {
  if (!data?.length > 0) return [];
  let dataCopy = [...(data ?? [])];

  if (!groupByColumns?.filter((e) => e !== null && e !== undefined)?.length) {
    return [
      dataCopy?.reduce((acc, cur) => {
        aggregateColumns.forEach((col) => {
          acc[col] = cur[col] > acc[col] ? cur[col] : acc[col] || cur[col] || 0;
        });
        return acc;
      }, {}),
    ];
  }

  return Object.values(
    dataCopy?.reduce((acc, cur) => {
      const key = groupByColumns?.map((col) => cur[col]).join("|");
      if (!acc[key]) {
        acc[key] = Object.fromEntries(groupByColumns?.map((col) => [col, cur[col]]));
        aggregateColumns.forEach((col) => {
          acc[key][col] = cur[col] || 0;
        });
      } else {
        aggregateColumns.forEach((col) => {
          acc[key][col] = cur[col] > acc[key][col] ? cur[col] : acc[key][col] || cur[col] || 0;
        });
      }
      return acc;
    }, {})
  );
};

export const aggregatedMinValue = ({ aggregateColumns, data, groupByColumns }) => {
  if (!data?.length > 0) return [];
  let dataCopy = [...(data ?? [])];

  if (!groupByColumns?.filter((e) => e !== null && e !== undefined)?.length) {
    return [
      dataCopy?.reduce((acc, cur) => {
        aggregateColumns.forEach((col) => {
          acc[col] = cur[col] < acc[col] ? cur[col] : acc[col] || cur[col] || 0;
        });
        return acc;
      }, {}),
    ];
  }

  return Object.values(
    dataCopy?.reduce((acc, cur) => {
      const key = groupByColumns?.map((col) => cur[col]).join("|");
      if (!acc[key]) {
        acc[key] = Object.fromEntries(groupByColumns?.map((col) => [col, cur[col]]));
        aggregateColumns.forEach((col) => {
          acc[key][col] = cur[col] || 0;
        });
      } else {
        aggregateColumns.forEach((col) => {
          acc[key][col] = cur[col] < acc[key][col] ? cur[col] : acc[key][col] || cur[col] || 0;
        });
      }
      return acc;
    }, {})
  );
};

export const pivotData = (data, pivotColumn, valueColumn) => {
  if (!data?.length || !pivotColumn) return [];
  const pivotedData = {};
  const keyColumns = Object.keys(data[0]).filter((col) => col !== pivotColumn && col !== valueColumn);
  data?.forEach((entry) => {
    const key = keyColumns?.map((col) => entry[col]).join("-");
    if (!pivotedData[key]) {
      pivotedData[key] = {};
      keyColumns?.forEach((col) => {
        pivotedData[key][col] = entry[col];
      });
    }
    pivotedData[key][entry[pivotColumn]] = entry[valueColumn];
  });
  return Object.values(pivotedData);
};

export const unpivotData = ({ columnsToUnpivot, data, newColumnName, valueName }) => {
  const flattenData = [];
  for (const row of data) {
    const rowCopy = { ...row };
    for (const col of columnsToUnpivot) delete rowCopy[col];
    for (const key of Object.keys(row)) {
      if (columnsToUnpivot?.includes(key)) {
        const flatItem = {
          ...rowCopy,
          [newColumnName]: key,
          [valueName]: row[key],
        };
        flattenData.push(flatItem);
      }
    }
  }
  return flattenData;
};

export const useDynamicDashboard = ({ chartsConfigs, defaultChart, defaultDashboardConfigs }) => {
  const [allChartsConfigs, setAllChartsConfigs] = useState(chartsConfigs);
  const scrollRef = useRef();

  const addChart = () => {
    const maxY = allChartsConfigs?.length ? Math.max(...allChartsConfigs?.map((l) => l.y + l.h)) : -1;
    const maxId = allChartsConfigs?.length ? Math.max(...allChartsConfigs?.map((l) => l.id)) : -1;
    const newChart = {
      ...defaultChart,
      id: maxId + 1,
      y: maxY + 1,
    };

    setAllChartsConfigs([...allChartsConfigs, newChart]);
    scrollRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  };

  const removeChart = (id) => {
    setAllChartsConfigs((allChartsConfigs) => allChartsConfigs.filter((l) => l.id !== id));
  };

  const updateChart = (id, key, value) => {
    setAllChartsConfigs((allChartsConfigs) => {
      const updatedCharts = allChartsConfigs.map((l) => {
        if (l.id !== id) return l;
        else return { ...l, [key]: value };
      });
      return updatedCharts;
    });
  };

  const resetDashboardConfigs = () => {
    setAllChartsConfigs(defaultDashboardConfigs);
  };

  return {
    addChart,
    allChartsConfigs,
    removeChart,
    resetDashboardConfigs,
    scrollRef,
    setAllChartsConfigs,
    updateChart,
  };
};

export const useDynamicDashboardMultiTab = ({ allTabschartsConfigs, defaultChart, defaultDashboardConfigs }) => {
  const [allTabsAllChartsConfigs, setAllTabsAllChartsConfigs] = useState(allTabschartsConfigs);
  const scrollRef = useRef();

  const tabAddChart = (tabIndex) => () => {
    const tabAllChartsConfigs = allTabsAllChartsConfigs[tabIndex];
    const maxY = tabAllChartsConfigs?.length ? Math.max(...tabAllChartsConfigs?.map((l) => l.y + l.h)) : -1;
    const maxId = tabAllChartsConfigs?.length ? Math.max(...tabAllChartsConfigs?.map((l) => l.id)) : -1;
    const newChart = {
      ...defaultChart,
      id: maxId + 1,
      y: maxY + 1,
    };

    const newAllTabsChartsConfigs = allTabsAllChartsConfigs.map((item, index) => {
      if (index !== tabIndex) return item;
      return [...item, newChart];
    });

    setAllTabsAllChartsConfigs(newAllTabsChartsConfigs);
    scrollRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  };

  const tabRemoveChart = (tabIndex) => (id) => {
    setAllTabsAllChartsConfigs((allTabsAllChartsConfigs) => {
      const filteredAllTabsChartsConfigs = allTabsAllChartsConfigs.map((item, index) => {
        if (index !== tabIndex) return item;
        return item.filter((l) => l.id !== id);
      });
      return filteredAllTabsChartsConfigs;
    });
  };

  const tabUpdateChart = (tabIndex) => (id, key, value) => {
    setAllTabsAllChartsConfigs((allTabsAllChartsConfigs) => {
      const updatedAllTabsChartsConfigs = allTabsAllChartsConfigs.map((item, index) => {
        if (index !== tabIndex) return item;
        const updatedCharts = item.map((l) => {
          if (l.id !== id) return l;
          else return { ...l, [key]: value };
        });
        return updatedCharts;
      });
    });
  };

  const resetDashboardConfigs = () => {
    setAllTabsAllChartsConfigs(defaultDashboardConfigs);
  };

  return {
    allTabsAllChartsConfigs,
    resetDashboardConfigs,
    scrollRef,
    setAllTabsAllChartsConfigs,
    tabAddChart,
    tabRemoveChart,
    tabUpdateChart,
  };
};

export const getMapPointValueBasedColor = ({ colorScale, max, min, opacity = 1, value }) => {
  value = Math.max(min, Math.min(value, max));

  const ratio = min === max ? 1 : (value - min) / (max - min);
  if (colorScale) {
    const scaled = colorScale(ratio);
    const rgb = scaled._rgb;

    const r = Math.round(255 * (1 - ratio));
    const g = Math.round(255 * ratio);
    const b = 0;

    return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${opacity})`;
  } else return "rgb(0,0,0,1)";
};

export const getAxisNameWithUnit = ({ dataKey, unit }) => {
  return `${dataKey ? dataKey : ""} ${unit ? "(" + unit + ")" : ""}`;
};

export const sortData = ({ data, dataKey, decsending }) => {
  if (!data?.length) return data;
  const sorted = [...data];
  sorted.sort((a, b) => {
    if (decsending) return a[dataKey] < b[dataKey] ? 1 : -1;
    else return a[dataKey] < b[dataKey] ? -1 : 1;
  });
  return sorted;
};

export const sortDataAccumulative = ({ data, dataKeys, decsending }) => {
  if (!data?.length || !dataKeys?.length) return data;
  const sorted = [...data];
  sorted.sort((a, b) => {
    const accA = sum(dataKeys.map((key) => a[key]));
    const accB = sum(dataKeys.map((key) => b[key]));
    if (decsending) return accA < accB ? 1 : -1;
    else return accA < accB ? -1 : 1;
  });
  return sorted;
};

const predefinedSteps = [
  0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 25, 50, 100, 250, 500, 1000, 2000, 5000, 10000,
  25000, 50000, 100000, 250000, 500000, 1000000, 2500000, 5000000, 10000000, 25000000, 50000000,
];

export const generateAxisValues = (minValue, maxValue, step) => {
  if (minValue === undefined || maxValue === undefined) return;
  let bestStep;
  const range = maxValue - minValue;
  if (step !== undefined) bestStep = step;
  else {
    const ticksNos = [...Array(5).keys()]?.map((e) => e + 5);
    const upperBounds = predefinedSteps?.map((step) => {
      for (const tickNo of ticksNos) {
        if (step * tickNo > range) return step * tickNo;
      }
      return null;
    });
    const bestStepIndex = upperBounds.indexOf(Math.min(...upperBounds.filter((e) => e !== null)));
    bestStep = predefinedSteps[bestStepIndex];
  }
  if (bestStep === undefined || bestStep === null) return;
  const axisValues = [];
  const roundedMinValue = Math.floor(minValue / bestStep) * bestStep;
  for (let i = 0; i <= Math.ceil(range / bestStep) + 2; i++) {
    axisValues.push(roundedMinValue + i * bestStep);
    if (roundedMinValue + i * bestStep >= maxValue) break;
  }
  return axisValues;
};

export const getAllDataEntities = ({ categoricalColumns, data, numericalColumns }) => {
  const entities = {};
  entities[colorCategoriesEnums.COLUMNS] = {};
  numericalColumns?.forEach((col) => {
    entities[colorCategoriesEnums.COLUMNS][col] = undefined;
  });

  entities[colorCategoriesEnums.VALUES] = {};
  categoricalColumns?.forEach((col) => {
    const columnUniqueValues = [...new Set(data?.map((row) => row[col]))];
    if (columnUniqueValues?.filter((e) => e !== undefined && e !== null)?.length > 0) {
      entities[colorCategoriesEnums.VALUES][col] = {};
      columnUniqueValues?.forEach((val) => {
        entities[colorCategoriesEnums.VALUES][col][val] = undefined;
      });
    }
  });
  return entities;
};

export const getColorEntity = ({ chartType, dataPoint, diagrams, quantity }) => {
  let categoryName, subcategoryName;
  let entityName = typeof quantity === "string" ? quantity || valuesEnums.NO_DATA : quantity ?? valuesEnums.NO_DATA;

  if (!chartType) return;

  if ([chartTypesEnums.MINI_MAP].includes(chartType)) {
    categoryName = colorCategoriesEnums.VALUES;
    subcategoryName = dataPoint;
  }

  if (!diagrams) return { categoryName, entityName, subcategoryName };

  let isMixed, isStacked, partitions, stacks;
  if (diagrams?.length) {
    stacks = diagrams[0]?.stacks;
    partitions = diagrams[0]?.partitions;
    isMixed = partitions && stacks;
    isStacked = partitions || stacks;
  }

  if (
    [chartTypesEnums.COMPOSED, chartTypesEnums.HEATMAP, chartTypesEnums.HISTOGRAM, chartTypesEnums.SCATTER].includes(
      chartType
    )
  ) {
    categoryName = colorCategoriesEnums.COLUMNS;
  }

  if ([chartTypesEnums.AREA, chartTypesEnums.LINE].includes(chartType)) {
    categoryName = partitions ? colorCategoriesEnums.VALUES : colorCategoriesEnums.COLUMNS;
    subcategoryName = partitions ? partitions : "";
  }

  if ([chartTypesEnums.BAR, chartTypesEnums.DOUGHNUT, chartTypesEnums.KPI, chartTypesEnums.PIE].includes(chartType)) {
    if (isMixed) {
      categoryName = colorCategoriesEnums.MIXED;
      subcategoryName = `(${stacks})-(${partitions})`;
    } else if (isStacked) {
      categoryName = colorCategoriesEnums.VALUES;
      subcategoryName = stacks ?? partitions;
    } else {
      categoryName = colorCategoriesEnums.COLUMNS;
      subcategoryName = "";
    }
  }

  return { categoryName, entityName, subcategoryName };
};

export const extractPartitionsAndStacksName = (input) => {
  const pattern = /^\((\w+)\)-\((\w+)\)$/;
  const match = input.match(pattern);

  if (match?.length > 2) {
    return [match[1], match[2]];
  }
  return null;
};

export const getColumnDisplayName = ({ colName, dataMapping }) => {
  try {
    const defaultOutput = dataMapping[colName];
    if (defaultOutput !== undefined && defaultOutput !== null) return defaultOutput;
    if (colName !== undefined && colName !== null) {
      const partitionStacked = extractPartitionsAndStacksName(colName);
      if (partitionStacked?.length > 1) {
        const stack = dataMapping[partitionStacked[0]] ?? capitalizeFirstLetter(partitionStacked[0]);
        const partition = dataMapping[partitionStacked[1]] ?? capitalizeFirstLetter(partitionStacked[1]);
        return partition && stack ? `(${stack})-(${partition})` : capitalizeFirstLetter(colName);
      } else if (colName.includes("___") && colName.includes(":")) {
        return getSeriesMixedDisplayName({ dataMapping, seriesMixedName: colName });
      }
      return capitalizeFirstLetter(colName);
    }
  } catch {
    return colName;
  }
};

export const getSeriesMixedName = ({ filters, seriesName }) => {
  const hasFilters = filters?.length;
  if (!hasFilters) return seriesName;
  else {
    const filtersDisplayName = filters?.map(
      ([filterName, filterValue]) => `${filterName.replace("diagramFilter_", "")}:${filterValue}`
    );
    return seriesName + "___" + filtersDisplayName?.join("___");
  }
};

export const getSeriesMixedDisplayName = ({ dataMapping, seriesMixedName }) => {
  let seriesParts = seriesMixedName.split("___");
  if (seriesParts?.length > 1) {
    const qunatity = seriesParts.shift();
    const filtersDisplayName = seriesParts?.map((filter) => {
      const [filterName, filterValue] = filter.split(":");
      return `${capitalizeFirstLetter(dataMapping[filterName] ?? filterName)}:${capitalizeFirstLetter(
        dataMapping[filterValue] ?? filterValue
      )}`;
    });
    return capitalizeFirstLetter(dataMapping[qunatity] ?? qunatity) + "___" + filtersDisplayName?.join("___");
  } else {
    return seriesMixedName;
  }
};

export const getDataKeyFromDiagramFilterName = ({ filterName }) => {
  return filterName.replace(valuesEnums.DIAGRAM_FILTER_PREFIX, "");
};

export const dynamicDashboardRelevantConfigOptions = Object.freeze({
  [chartTypesEnums.AREA]: [
    settingsOptions.X_AXIS,
    settingsOptions.PARTITIONS,
    settingsOptions.HEIGHT_100,
    settingsOptions.DIRECTION,
    settingsOptions.LEGENDS_POSITION,
    settingsOptions.SORT_BY,
    settingsOptions.DATA_POINT,
    settingsOptions.TITLE,
    settingsOptions.INVERT_X_AXIS,
    settingsOptions.INVERT_Y_AXIS,
    settingsOptions.AGGREGATION_TYPE,
    settingsOptions.SCALING_TYPE,
    settingsOptions.Y_AXIS_NAME,
    settingsOptions.X_AXIS_NAME,
    settingsOptions.DESCRIPTION,
    settingsOptions.Y_AXIS_RANGE,
  ],
  [chartTypesEnums.BAR]: [
    settingsOptions.X_AXIS,
    settingsOptions.PARTITIONS,
    settingsOptions.STACKS,
    settingsOptions.DIRECTION,
    settingsOptions.LEGENDS_POSITION,
    settingsOptions.SORT_BY,
    settingsOptions.DATA_POINT,
    settingsOptions.TITLE,
    settingsOptions.INVERT_X_AXIS,
    settingsOptions.INVERT_Y_AXIS,
    settingsOptions.AGGREGATION_TYPE,
    settingsOptions.SCALING_TYPE,
    settingsOptions.Y_AXIS_NAME,
    settingsOptions.X_AXIS_NAME,
    settingsOptions.DESCRIPTION,
    settingsOptions.Y_AXIS_RANGE,
  ],
  [chartTypesEnums.COMPOSED]: [
    settingsOptions.TITLE,
    settingsOptions.X_AXIS,
    settingsOptions.DIAGRAMS_DIAGRAM_DATA_KEY,
    settingsOptions.SORT_BY,
    settingsOptions.DIAGRAM_TYPE,
    settingsOptions.COLOR,
    settingsOptions.LEGENDS_POSITION,
    settingsOptions.INVERT_X_AXIS,
    settingsOptions.INVERT_Y_AXIS,
    settingsOptions.AGGREGATION_TYPE,
    settingsOptions.SCALING_TYPE,
    settingsOptions.Y_AXIS_NAME,
    settingsOptions.X_AXIS_NAME,
    settingsOptions.DIAGRAMS_STACK_ID,
    settingsOptions.DESCRIPTION,
    settingsOptions.DIAGRAMS_AS_SECOND_Y_AXIS,
    settingsOptions.SECOND_Y_AXIS_NAME,
    settingsOptions.X_AXIS_RANGE,
    settingsOptions.Y_AXIS_RANGE,
    settingsOptions.SECOND_Y_AXIS_RANGE,
  ],
  [chartTypesEnums.DOUGHNUT]: [
    settingsOptions.PARTITIONS,
    settingsOptions.STACKS,
    settingsOptions.TITLE,
    settingsOptions.LEGENDS_POSITION,
    settingsOptions.DATA_POINT,
    settingsOptions.SHOW_PIE_LABELS,
    settingsOptions.AGGREGATION_TYPE,
    settingsOptions.SCALING_TYPE,
    settingsOptions.DESCRIPTION,
  ],
  [chartTypesEnums.HEATMAP]: [
    settingsOptions.TITLE,
    settingsOptions.X_AXIS,
    settingsOptions.Y_AXIS,
    settingsOptions.COLOR,
    settingsOptions.DATA_POINT,
    settingsOptions.INVERT_X_AXIS,
    settingsOptions.INVERT_Y_AXIS,
    settingsOptions.AGGREGATION_TYPE,
    settingsOptions.SCALING_TYPE,
    settingsOptions.Y_AXIS_NAME,
    settingsOptions.X_AXIS_NAME,
    settingsOptions.DESCRIPTION,
  ],
  [chartTypesEnums.HISTOGRAM]: [
    settingsOptions.TITLE,
    settingsOptions.DIAGRAMS_DIAGRAM_DATA_KEY,
    settingsOptions.SORT_BY,
    settingsOptions.COLOR,
    settingsOptions.LEGENDS_POSITION,
    settingsOptions.INVERT_X_AXIS,
    settingsOptions.INVERT_Y_AXIS,
    settingsOptions.SCALING_TYPE,
    settingsOptions.X_AXIS_NAME,
    settingsOptions.DESCRIPTION,
  ],
  [chartTypesEnums.KPI]: [
    settingsOptions.TITLE,
    settingsOptions.PARTITIONS,
    settingsOptions.STACKS,
    settingsOptions.DIRECTION,
    settingsOptions.DATA_POINT,
    settingsOptions.AGGREGATION_TYPE,
    settingsOptions.SCALING_TYPE,
    settingsOptions.DESCRIPTION,
  ],
  [chartTypesEnums.LINE]: [
    settingsOptions.TITLE,
    settingsOptions.X_AXIS,
    settingsOptions.PARTITIONS,
    settingsOptions.SORT_BY,
    settingsOptions.DIRECTION,
    settingsOptions.LEGENDS_POSITION,
    settingsOptions.DATA_POINT,
    settingsOptions.INVERT_X_AXIS,
    settingsOptions.INVERT_Y_AXIS,
    settingsOptions.AGGREGATION_TYPE,
    settingsOptions.SCALING_TYPE,
    settingsOptions.Y_AXIS_NAME,
    settingsOptions.X_AXIS_NAME,
    settingsOptions.DESCRIPTION,
    settingsOptions.Y_AXIS_RANGE,
  ],
  [chartTypesEnums.MINI_MAP]: [
    settingsOptions.DATA_POINT,
    settingsOptions.COLOR_BAR_RANGE,
    settingsOptions.AGGREGATION_TYPE,
    settingsOptions.SCALING_TYPE,
    settingsOptions.DESCRIPTION,
    settingsOptions.TITLE,
  ],
  [chartTypesEnums.PIE]: [
    settingsOptions.PARTITIONS,
    settingsOptions.STACKS,
    settingsOptions.TITLE,
    settingsOptions.LEGENDS_POSITION,
    settingsOptions.DATA_POINT,
    settingsOptions.SHOW_PIE_LABELS,
    settingsOptions.AGGREGATION_TYPE,
    settingsOptions.SCALING_TYPE,
    settingsOptions.DESCRIPTION,
  ],
  [chartTypesEnums.SCATTER]: [
    settingsOptions.TITLE,
    settingsOptions.X_AXIS,
    settingsOptions.DIAGRAMS_DIAGRAM_DATA_KEY,
    settingsOptions.COLOR,
    settingsOptions.LEGENDS_POSITION,
    settingsOptions.INVERT_X_AXIS,
    settingsOptions.INVERT_Y_AXIS,
    settingsOptions.SCALING_TYPE,
    settingsOptions.Y_AXIS_NAME,
    settingsOptions.X_AXIS_NAME,
    settingsOptions.DESCRIPTION,
    settingsOptions.DIAGRAMS_AS_SECOND_Y_AXIS,
    settingsOptions.SECOND_Y_AXIS_NAME,
    settingsOptions.X_AXIS_RANGE,
    settingsOptions.Y_AXIS_RANGE,
    settingsOptions.SECOND_Y_AXIS_RANGE,
  ],
  [chartTypesEnums.SUMMARY]: [settingsOptions.DATA_POINT, settingsOptions.TITLE, settingsOptions.DESCRIPTION],
});

const isRelevantOption = (chartType, option, index) => {
  if (
    index > 0 &&
    chartType !== chartTypesEnums.HISTOGRAM &&
    chartType !== chartTypesEnums.COMPOSED &&
    chartType !== chartTypesEnums.SCATTER
  )
    return false;
  return dynamicDashboardRelevantConfigOptions[chartType]?.includes(option);
};

export const getArrayLongestElement = (array) => {
  if (!array?.filter((e) => e !== undefined && e !== null)) return;
  if (array?.some((e) => typeof e === "string"))
    return array?.reduce((total, current) => (total?.length > current?.length ? total : current), "");
  else if (array?.some((e) => typeof e === "boolean")) return 5;
  else if (array?.some((e) => typeof e === "number")) return Math.max(...array?.map((s) => s ?? 0));
  else return 5;
};

const convertStringRangeToNumberRange = (rangeString) => {
  return rangeString?.split(" - ")?.map((numString) => parseFloat(numString?.replaceAll(" ", "")?.replaceAll(",", "")));
};

export const dynamicDashboardGetRelatedItems = ({ data, hoveredItem, IdColumnName }) => {
  if (!hoveredItem) return;
  const { config, params, point } = hoveredItem;
  const { chartType, diagrams, xAxis, yAxis } = config ?? {};
  const diagramsDataKey = point?.diagramsDataKey;

  let isMixed, isStacked, partitions, stacks;
  if (diagrams?.length) {
    stacks = diagrams[0]?.stacks;
    partitions = diagrams[0]?.partitions;
    isMixed = partitions && stacks;
    isStacked = partitions || stacks;
  }

  if (chartType === chartTypesEnums.MINI_MAP) {
    const actualPoint = data?.filter((row) => row?.[IdColumnName] === point?.[IdColumnName]);

    return actualPoint || [];
  }
  if (chartType === chartTypesEnums.HISTOGRAM) {
    const { dataPoint, index, rangeString } = point;
    const valueRange = convertStringRangeToNumberRange(rangeString);
    const [min, max] = valueRange ?? [];
    return data.filter((row) => {
      const value = row[dataPoint];
      return (value > min || (value === min && index === 0)) && value <= max;
    });
  }
  return data?.filter((row) =>
    areItemsSame({ chartType, diagrams, diagramsDataKey, item1: point, item2: row, xAxis, yAxis })
  );
};

export const dynamicDashboardIsItemHovered = ({
  chartType,
  diagrams,
  diagramsDataKey,
  hoveredItem,
  hoveredItemRelatedItems,
  IdColumnName,
  row,
  xAxis,
  yAxis,
}) => {
  let isMixed, isStacked, partitions, stacks;
  if (diagrams?.length) {
    stacks = diagrams[0]?.stacks;
    partitions = diagrams[0]?.partitions;
    isMixed = partitions && stacks;
    isStacked = partitions || stacks;
  }
  const relatedItems = hoveredItemRelatedItems;

  if (chartType === chartTypesEnums.MINI_MAP && hoveredItem?.config?.chartType === chartTypesEnums.MINI_MAP)
    return true;
  else if (chartType === chartTypesEnums.MINI_MAP) {
    return relatedItems?.some((item) => item[IdColumnName] === row?.[IdColumnName]);
  } else if (chartType === chartTypesEnums.HISTOGRAM) {
    const { dataPoint, index, rangeString } = row ?? {};
    const interval = convertStringRangeToNumberRange(rangeString);
    const [min, max] = interval ?? [];
    return relatedItems?.some((item) => {
      const value = item[dataPoint];
      return (value > min || (value === min && index === 0)) && value <= max;
    });
  } else {
    return relatedItems?.some((item) =>
      areItemsSame({ chartType, diagrams, diagramsDataKey, item1: item, item2: row, xAxis, yAxis })
    );
  }
};

export const getItemsEqualityCriteriaMapping = ({ diagramsDataKey, partitions, stacks, xAxis, yAxis }) => {
  const mapping = {
    [settingsOptions.DIAGRAMS_DIAGRAM_DATA_KEY]: diagramsDataKey,
    [settingsOptions.PARTITIONS]: partitions,
    [settingsOptions.STACKS]: stacks,
    [settingsOptions.X_AXIS]: xAxis,
    [settingsOptions.Y_AXIS]: yAxis,
  };
  return mapping;
};

const areItemsSame = ({ chartType, diagrams, diagramsDataKey, item1, item2, xAxis, yAxis }) => {
  const { partitions, stacks } = diagrams?.[0] ?? {};

  const settingsMapping = getItemsEqualityCriteriaMapping({ diagramsDataKey, partitions, stacks, xAxis, yAxis });
  for (const [settingName, settingValue] of Object.entries(settingsMapping)) {
    if (isRelevantOption(chartType, settingName) && !(item1?.[settingValue] === item2?.[settingValue] || !settingValue))
      return false;
  }

  return true;
};

export const echartsDefaultTooltipFormatter = (value) => formatNumberBasedOnUser(value);

export const aggregationFunctionConfig = {
  [aggregationFunctionsTypes.AVG]: aggregatedAvgValue,
  [aggregationFunctionsTypes.MAX]: aggregatedMaxValue,
  [aggregationFunctionsTypes.MIN]: aggregatedMinValue,
  [aggregationFunctionsTypes.SUM]: aggregatedSumValue,
};

const CHART_PALETTE_FAMILIES = [
  "secondary-blue",
  "mystic-red",
  "vivid-orchid",
  "plunge",
  "star",
  "grey",
  "leaftech-blue",
  "tangerine",
  "cathode-green",
  "bright-indigo",
];

const CHART_PALETTE_SHADES = [600, 400, 200, 800];

const flatColors = CHART_PALETTE_SHADES.flatMap((shade) =>
  CHART_PALETTE_FAMILIES.map((family) => `--core-color-${family}-${shade}`)
);

const stackedColors = CHART_PALETTE_SHADES.map((shade) =>
  CHART_PALETTE_FAMILIES.map((family) => `--core-color-${family}-${shade}`)
);

export const getItemColor = (stackIdx, partitionIdx) => {
  var style = getComputedStyle(document.documentElement);
  const isStacked = stackIdx >= 0;
  const isPartitioned = partitionIdx >= 0;

  if (!isStacked && !isPartitioned) {
    return style.getPropertyValue(flatColors[0]).trim();
  }
  if (!(isStacked && isPartitioned)) {
    return style.getPropertyValue(flatColors[(stackIdx ?? partitionIdx) % flatColors.length]).trim();
  }
  const validStackIdx = stackIdx % stackedColors.length;
  const validPartitionIdx = partitionIdx % stackedColors[0].length;

  return style.getPropertyValue(stackedColors[validStackIdx][validPartitionIdx]);
};

export const getTabFiltersCreators = ({ allFiltersConfig, columnsDisplayNameFn, generalDashboardData, setConfig }) => {
  const getTabFiltersItemCommonPart = (colName) => {
    const columnsDisplayName = columnsDisplayNameFn({ colName });
    return {
      config: allFiltersConfig,
      filterDisplayName: columnsDisplayName,
      filterName: colName,
      mainDataKey: colName,
      props: {
        className: "w-full",
        height: "4.8rem",
        label: columnsDisplayName,
        width: "100%",
      },
      setConfig,
      visible: () => true,
    };
  };

  const createDropdownFilter = (colName) => {
    const commonPart = getTabFiltersItemCommonPart(colName);
    return {
      ...(commonPart ?? {}),
      filterType: FilterTypes.DROPDOWN_MULTI,
      props: {
        ...(commonPart?.props ?? {}),
        options: getUniqueValuesFromJson(generalDashboardData, colName),
      },
    };
  };

  const createNumericRangeFilter = (colName) => {
    const commonPart = getTabFiltersItemCommonPart(colName);
    return {
      ...(commonPart ?? {}),
      filterType: FilterTypes.NUMERIC_RANGE_PICKER,
      props: {
        ...(commonPart?.props ?? {}),
        ...findDataRange({
          data: generalDashboardData,
          dataKeys: [colName],
          defaultMin: 0,
        }),
      },
    };
  };

  return { createDropdownFilter, createNumericRangeFilter, getTabFiltersItemCommonPart };
};

export const getChartFiltersCreators = ({ columnsDisplayNameFn, config, generalDashboardData, updateChart }) => {
  const getChartFiltersItemCommonPart = (colName) => {
    const columnsDisplayName = columnsDisplayNameFn({ colName });
    return {
      adjustConfig: getIndividualChartFitlersConfig,
      filterDisplayName: columnsDisplayName,
      filterName: colName,
      mainDataKey: colName,
      props: {
        className: "w-full",
        label: columnsDisplayName,
        width: "100%",
      },
      setConfig: (key, value) =>
        updateChart(INDIVIDUAL_CHART_FILTERS_KEY, { ...config?.[INDIVIDUAL_CHART_FILTERS_KEY], [key]: value }),
      visible: () => true,
    };
  };

  const createDropdownFilter = (key) => {
    const commonPart = getChartFiltersItemCommonPart(key);
    return {
      ...(commonPart ?? {}),
      filterType: FilterTypes.DROPDOWN_MULTI,
      props: {
        ...(commonPart?.props ?? {}),
        options: getUniqueValuesFromJson(generalDashboardData, key),
      },
    };
  };

  const createNumericRangeFilter = (colName) => {
    const commonPart = getChartFiltersItemCommonPart(colName);
    return {
      ...(commonPart ?? {}),
      filterType: FilterTypes.NUMERIC_RANGE_PICKER,
      props: {
        ...(commonPart?.props ?? {}),
        changeByTyping: true,
        ...findDataRange({
          data: generalDashboardData,
          dataKeys: [colName],
          defaultMin: 0,
        }),
      },
    };
  };
  return {
    createDropdownFilter,
    createNumericRangeFilter,
    getChartFiltersItemCommonPart,
  };
};

export const defaultSortFunction = (a, b) => {
  return a < b ? -1 : 1;
};

export const summaryTypesNames = Object.freeze({
  COUNT_UNIQUE_VALUES: "count_unique_values",
  DOMINANT_AGGREGATED_CATEGORY: "dominant_aggregated_category",
  RANGE: "range",
  SUM: "sum",
  VALUE_COVERAGE: "value_coverage",
});

export const summaryTypes = Object.freeze({
  [summaryTypesNames.COUNT_UNIQUE_VALUES]: {
    aggregatorFunction: ({ aggregateColumn, data }) => {
      return [...new Set(data?.map((row) => row[aggregateColumn]))]?.length;
    },
  },
  [summaryTypesNames.DOMINANT_AGGREGATED_CATEGORY]: {
    aggregatorFunction: ({ aggregateColumn, data, groupByColumn }) => {
      const aggregatedCategories = aggregatedSumValue({
        aggregateColumns: [aggregateColumn],
        data,
        groupByColumns: [groupByColumn],
      });

      let dominantCategory = null;

      aggregatedCategories?.forEach((category) => {
        if (!dominantCategory || category[aggregateColumn] > dominantCategory?.[aggregateColumn]) {
          dominantCategory = category;
        }
      });

      return dominantCategory?.[groupByColumn];
    },
  },
  [summaryTypesNames.RANGE]: {
    aggregatorFunction: ({ aggregateColumn, data }) => [
      min(data?.map((row) => row[aggregateColumn] ?? 0) ?? []),
      max(data?.map((row) => row[aggregateColumn] ?? 0) ?? []),
    ],
    displayFunction: ({ value }) => value?.map((v) => formatNumberBasedOnUser(v)).join(" - "),
  },
  [summaryTypesNames.TOTAL]: {
    aggregatorFunction: ({ aggregateColumn, data }) => sum(data?.map((row) => row[aggregateColumn] ?? 0) ?? []),
    displayFunction: ({ value }) => (value ? formatNumberBasedOnUser(value) : 0),
  },
  [summaryTypesNames.VALUE_COVERAGE]: {
    aggregatorFunction: ({ aggregateColumn, data, filterFunction }) => {
      const total = sum(data?.map((row) => row[aggregateColumn]));
      if (!total) return 0;
      return sum(data?.filter((row) => filterFunction(row))?.map((row) => row[aggregateColumn] ?? 0)) / total;
    },
    displayFunction: ({ value }) => `${formatNumberBasedOnUser(100 * value)}%`,
  },
});

const isEmptyColorConfigValue = (value) => value == null || value === "";

const isPlainColorConfigObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

export const mergeNestedColorConfigValues = (a, b) => {
  if (isPlainColorConfigObject(a) && isPlainColorConfigObject(b)) {
    const merged = {};
    new Set([...Object.keys(a), ...Object.keys(b)]).forEach((key) => {
      merged[key] = mergeNestedColorConfigValues(a[key], b[key]);
    });
    return merged;
  }

  if (isPlainColorConfigObject(a)) {
    return isEmptyColorConfigValue(b) ? a : b;
  }

  if (isPlainColorConfigObject(b)) {
    return isEmptyColorConfigValue(a) ? b : a;
  }

  const aEmpty = isEmptyColorConfigValue(a);
  const bEmpty = isEmptyColorConfigValue(b);
  if (aEmpty && !bEmpty) return b;
  if (bEmpty && !aEmpty) return a;
  return bEmpty ? a : b;
};
